import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { getColors } from '../../styles/colors';

const OrderSummary = () => {
  const { orderItems, totalPrice, selectedDate, selectedTime } = useSelector((state) => state.tiffin);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);

  if (orderItems.length === 0) {
    return (
      <div 
        className="rounded-2xl p-6 text-center"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <h2 className="text-xl font-bold mb-4" style={{ color: colors.text }}>📋 Order Summary</h2>
        <p className="text-4xl mb-4">🛒</p>
        <p style={{ color: colors.textSecondary }}>No items selected yet</p>
        <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>Browse meals and add items</p>
      </div>
    );
  }

  // Group items by scheduled date
  const groupedItems = orderItems.reduce((acc, item) => {
    const date = item.scheduledDate || 'Unassigned';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (dateStr === 'Unassigned') return 'Unassigned';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`
      }}
    >
      <h2 className="text-xl font-bold mb-4" style={{ color: colors.text }}>📋 Order Summary</h2>
      
      <div className="max-h-64 overflow-y-auto space-y-3">
        {Object.entries(groupedItems).map(([date, items]) => (
          <div key={date}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold" style={{ color: colors.primary }}>
                📅 {formatDate(date)}
              </span>
              <span className="text-xs" style={{ color: colors.textSecondary }}>
                ({items.length} items)
              </span>
            </div>
            {items.map((item, index) => (
              <motion.div
                key={`${item.id}-${index}`}
                className="flex items-center justify-between py-1 px-2 rounded-lg"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`
                }}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div>
                  <span className="text-sm font-medium" style={{ color: colors.text }}>
                    {item.name} x{item.quantity}
                  </span>
                  {item.scheduledTime && (
                    <span className="text-xs ml-2" style={{ color: colors.textSecondary }}>
                      🕐 {item.scheduledTime}
                    </span>
                  )}
                  {item.mealTypeSelected && (
                    <span className="text-xs ml-2" style={{ color: colors.textSecondary }}>
                      {item.mealTypeSelected}
                    </span>
                  )}
                </div>
                <span className="text-sm font-bold" style={{ color: colors.primary }}>
                  ₹{item.price * item.quantity}
                </span>
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      <div className="border-t mt-4 pt-4" style={{ borderColor: colors.border }}>
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold" style={{ color: colors.text }}>Total</span>
          <span className="text-2xl font-bold" style={{ color: colors.primary }}>₹{totalPrice}</span>
        </div>
        {selectedDate && selectedTime && (
          <div className="text-xs mt-2" style={{ color: colors.textSecondary }}>
            📅 {formatDate(selectedDate)} • 🕐 {selectedTime}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;